@file:Suppress("UNCHECKED_CAST_TO_EXTERNAL_INTERFACE")

package org.example.project.login

import org.example.project.settings.UISettings

import androidx.compose.runtime.*
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.await
import kotlinx.coroutines.launch
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.buildJsonObject
import org.example.project.dashboard.DashboardTheme
import org.example.project.datenschutz.DatenschutzTexte
import org.example.project.datenschutz.SignaturDatenschutzOverlay
import org.example.project.manager.LumaticaApiManager
import org.example.project.manager.LumaticaApiSettings
import org.example.project.manager.NodeScope
import org.example.project.service.LoginUserApi
import org.example.project.service.LoginService
import org.example.project.service.PasswordChangeApi
import org.example.project.service.PasswordPolicy
import org.example.project.service.PasswordResetApi
import org.example.project.settings.LogoSettings
import org.example.project.settings.PasswordRequirementHint
import org.example.project.utils.All
import org.example.project.utils.DashboardVerlaufHintergrund
import org.jetbrains.compose.web.ExperimentalComposeWebApi
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.w3c.dom.HTMLCanvasElement
import org.w3c.fetch.RequestInit

private const val CUS_MOBILE_CANVAS = "cus-mobile-signatur-canvas"

private val mobileLoginJson = Json {
    ignoreUnknownKeys = true
    encodeDefaults = false
}

private suspend fun saveMobileLoginSignatur(userId: String, signatur: String): Boolean {
    if (userId.isBlank() || signatur.isBlank()) return false
    return runCatching {
        window.fetch(
        "${LumaticaApiSettings.getBaseUrl()}/api/mobileloginscreen/signatur-save-json",
        RequestInit(
            method = "POST",
            headers = LumaticaApiManager.auditHeaders("application/json"),
            body = mobileLoginJson.encodeToString(
                buildJsonObject {
                    put("userId", JsonPrimitive(userId))
                    put("signatur", JsonPrimitive(signatur))
                }
            )
        )
        ).await().ok
    }.getOrDefault(false)
}

@Composable
fun MobileLoginScreen(
    art: String,
    onSubmit: (String, String) -> Unit
) {
    Style(MobileLoginStyles)
    Style(LoginStyles)
    Style(UnifiedAccessStyles)
    DashboardVerlaufHintergrund()

    val scope = rememberCoroutineScope()
    val loginRequestId = remember { newLoginRequestId() }

    var step     by remember { mutableStateOf("LOGIN") }
    var kennung by remember { mutableStateOf("") }
    var password by remember { mutableStateOf("") }
    var error    by remember { mutableStateOf(false) }
    var errorText by remember { mutableStateOf("") }
    var loading  by remember { mutableStateOf(false) }
    var resetLoading by remember { mutableStateOf(false) }
    var resetMessage by remember { mutableStateOf<String?>(null) }

    var pendingUser        by remember { mutableStateOf<All?>(null) }
    var pendingNewPassword by remember { mutableStateOf("") }
    var zweiFaktorCode by remember { mutableStateOf("") }
    var zweiFaktorToken by remember { mutableStateOf("") }
    var zweiFaktorEinrichtung by remember { mutableStateOf(false) }
    var zweiFaktorQrCode by remember { mutableStateOf("") }
    var trustedDevice by remember { mutableStateOf(false) }

    var newPassword    by remember { mutableStateOf("") }
    var repeatPassword by remember { mutableStateOf("") }
    var passwordError  by remember { mutableStateOf(false) }

    var canvasGezeichnet      by remember { mutableStateOf(false) }
    var datenschutzAkzeptiert by remember { mutableStateOf(false) }
    var signaturFehler        by remember { mutableStateOf(false) }

    fun completeLoginStep(user: All) {
        error = false
        pendingUser = user
        errorText = ""
        when {
            !user.passwortVerifiziert -> step = "PASSWORT"
            user.signatur.isBlank() || !user.datenschutzAkzeptiert -> step = "SIGNATUR"
            else -> {
                LoginService.login(user)
                onSubmit(kennung, password)
            }
        }
    }

    fun submitLogin() {
        if (loading) return
        if (step == "LOGIN" && kennung.isBlank()) {
            error = true
            errorText = if (art == NodeScope.SYS) "Bitte E-Mail eingeben." else "Bitte Personalnummer oder E-Mail eingeben."
            return
        }
        if (step == "LOGIN" && password.isBlank()) {
            error = true
            errorText = "Bitte Passwort eingeben."
            return
        }
        loading = true
        scope.launch {
            try {
                when (val result = UnifiedLoginFlow.submit(
                    LoginFlowRequest(
                        kennung = kennung,
                        password = password,
                        target = LoginTarget.MOBILE,
                        loginRequestId = loginRequestId,
                        zweiFaktorToken = if (step == "ZWEI_FAKTOR") zweiFaktorToken else "",
                        zweiFaktorCode = zweiFaktorCode,
                        trustedDevice = trustedDevice && !zweiFaktorEinrichtung
                    )
                )) {
                    is LoginFlowResult.Authenticated -> {
                        LoginService.captureSession(result.user)
                        completeLoginStep(result.user)
                    }
                    is LoginFlowResult.ZweiFaktor -> {
                        zweiFaktorToken = result.token
                        zweiFaktorEinrichtung = result.setup
                        zweiFaktorQrCode = result.qrCode
                        zweiFaktorCode = ""
                        trustedDevice = false
                        error = false
                        errorText = ""
                        step = "ZWEI_FAKTOR"
                    }
                    is LoginFlowResult.Rejected -> {
                        error = true
                        errorText = result.message
                    }
                }
            } finally {
                loading = false
            }
        }
    }

    fun requestPasswordReset() {
        val email = kennung.trim().lowercase()
        if (email.isBlank()) {
            error = true
            errorText = "Bitte E-Mail eingeben."
            resetMessage = null
            return
        }
        if (loading || resetLoading) return
        resetLoading = true
        error = false
        errorText = ""
        resetMessage = null
        scope.launch {
            try {
                if (PasswordResetApi.request(email, "desktop")) {
                    resetMessage = "Wenn ein Konto existiert, wurde eine E-Mail versendet."
                } else {
                    error = true
                    errorText = "E-Mail konnte nicht versendet werden. Bitte später erneut versuchen."
                }
            } finally {
                resetLoading = false
            }
        }
    }

    fun openRegistration() {
        window.location.href = if (window.location.hostname == "localhost") "http://localhost:2000/register" else "https://lumatica.de/register"
    }

    fun doChangePassword() {
        if (newPassword != repeatPassword || PasswordPolicy.error(newPassword) != null) { passwordError = true; return }
        passwordError = false
        pendingNewPassword = newPassword
        step = "SIGNATUR"
    }

    fun doCompleteOnboarding() {
        if (!canvasGezeichnet) { signaturFehler = true; return }
        signaturFehler = false
        scope.launch {
            val canvas = document.getElementById(CUS_MOBILE_CANVAS) as? HTMLCanvasElement
            if (canvas == null) { signaturFehler = true; return@launch }
            var user = (pendingUser ?: return@launch)
            if (pendingNewPassword.isNotBlank()) {
                user = PasswordChangeApi.changePassword(user.id, pendingNewPassword) ?: run {
                    signaturFehler = true
                    return@launch
                }
                pendingUser = user
            }
            val signatur = canvas.toDataURL("image/png")
            if (!saveMobileLoginSignatur(user.id, signatur)) {
                signaturFehler = true
                return@launch
            }
            val savedUser = LoginUserApi.saveUser(user.copy(datenschutzAkzeptiert = true, signatur = ""))
                ?: user.copy(datenschutzAkzeptiert = true, signatur = "")
            org.example.project.service.GlobalManager.triggerSessionConfigReload()
            LoginService.login(savedUser.copy(signatur = signatur, datenschutzAkzeptiert = true))
            onSubmit(kennung, pendingNewPassword.ifBlank { password })
        }
    }

    // Signatur-Schritt (Vollbild-Overlay)
    if (step == "SIGNATUR") {
        Div({ classes(MobileLoginStyles.backgroundStripes) })
        SignaturDatenschutzOverlay(
            canvasId = CUS_MOBILE_CANVAS,
            datenschutzText = DatenschutzTexte.SIGNATUR,
            datenschutzLabel = "Ich habe die Datenschutzhinweise gelesen und akzeptiere diese.",
            buttonLabel = "Profil abschließen und anmelden",
            canvasHoehePx = 130,
            maxWidthPx = 420,
            overlayStyle = {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.Center)
                alignItems(AlignItems.Center)
                backgroundColor(DashboardTheme.BACKGROUND_APP)
                property("background", DashboardTheme.BACKGROUND_APP_GRADIENT)
                property("z-index", "10")
                property("font-family", "system-ui, Segoe UI, Arial")
                padding(16.px)
                property("box-sizing", "border-box")
                property("overflow-y", "auto")
            },
            cardStyle = {
                backgroundColor(DashboardTheme.SURFACE_DEEP)
                property("border", "1px solid #2a2d34")
                borderRadius(16.px)
                padding(24.px)
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(16.px)
                width(100.percent)
                property("box-sizing", "border-box")
                property("box-shadow", "0 12px 48px rgba(0,0,0,0.8)")
            },
            canvasGezeichnet = canvasGezeichnet,
            onCanvasGezeichnetChange = { canvasGezeichnet = it },
            datenschutzAkzeptiert = datenschutzAkzeptiert,
            onDatenschutzAkzeptiertChange = { datenschutzAkzeptiert = it },
            fehlerText = if (signaturFehler) {
                if (!canvasGezeichnet) "Bitte zeichne deine Unterschrift." else "Signatur konnte nicht gespeichert werden."
            } else null,
            onComplete = { doCompleteOnboarding() }
        )
        return
    }

    if (step == "ZWEI_FAKTOR") {
        UnifiedLoginLayout(isMobile = true) {
            ZweiFaktorLoginUi(
                setup = zweiFaktorEinrichtung,
                qrCode = zweiFaktorQrCode,
                code = zweiFaktorCode,
                trustedDevice = trustedDevice,
                loading = loading,
                error = error,
                errorText = errorText,
                onCodeChange = {
                    zweiFaktorCode = it
                    error = false
                },
                onTrustedDeviceChange = { trustedDevice = it },
                onSubmit = ::submitLogin
            )
            UnifiedAccessButton(
                label = if (loading) "..." else "Code prüfen",
                enabled = !loading,
                onClick = ::submitLogin
            )
        }
        return
    }

    if (step == "LOGIN") {
        UnifiedLoginLayout(isMobile = true) {
            UnifiedLoginContent(
                identifier = kennung,
                password = password,
                identifierPlaceholder = if (art == NodeScope.SYS) "E-Mail" else "Personalnummer oder E-Mail",
                loading = loading,
                errorText = if (error) errorText else null,
                infoText = resetMessage,
                forgotLabel = if (art == NodeScope.SYS) (if (resetLoading) "..." else "Passwort vergessen") else null,
                onIdentifierChange = { kennung = it; error = false; resetMessage = null },
                onPasswordChange = { password = it; error = false },
                onSubmit = ::submitLogin,
                onForgot = if (art == NodeScope.SYS) ::requestPasswordReset else null
            )
        }
        return
    }

    // Login + Passwort-Schritt
    Div({ classes(MobileLoginStyles.backgroundStripes) })

    Main({ classes(MobileLoginStyles.stage, UnifiedAccessStyles.stage) }) {
        Div({ classes(MobileLoginStyles.contentWrapper, UnifiedAccessStyles.panel, UnifiedAccessStyles.mobilePanel) }) {

            Div({ classes(MobileLoginStyles.logoPane) }) {
                Img(src = LogoSettings.mobileLoginHeroLogo, attrs = {
                    classes(MobileLoginStyles.logoImg)
                    style {
                        width(LogoSettings.mobileLoginHeroWidthPx.px)
                        height(LogoSettings.mobileLoginHeroHeightPx.px)
                    }
                })
            }

            Div({ classes(MobileLoginStyles.formPane) }) {
                when (step) {
                    "LOGIN" -> {
                        UnifiedLoginContent(
                            identifier = kennung,
                            password = password,
                            identifierPlaceholder = if (art == NodeScope.SYS) "E-Mail" else "Personalnummer oder E-Mail",
                            loading = loading,
                            errorText = if (error) errorText else null,
                            forgotLabel = "Passwort vergessen",
                            switchPrefix = "Neu hier?",
                            switchLabel = "Registrieren",
                            onIdentifierChange = { kennung = it; error = false },
                            onPasswordChange = { password = it; error = false },
                            onSubmit = ::submitLogin,
                            onForgot = ::requestPasswordReset,
                            onSwitch = ::openRegistration
                        )
                    }

                    "PASSWORT" -> {
                        Div({ style { color(Color("#f0f1f3")); fontSize(14.px); fontWeight("bold"); textAlign("center") } }) {
                            Text("Passwort setzen")
                        }
                        Div({ style { color(Color("#8a8f9a")); fontSize(12.px); textAlign("center"); lineHeight("1.4"); marginBottom(4.px) } }) {
                            Text("Bitte lege ein persönliches Passwort fest.")
                        }
                        Input(type = InputType.Password, attrs = {
                            classes(MobileLoginStyles.input, UnifiedAccessStyles.input)
                            placeholder("Neues Passwort")
                            value(newPassword)
                            onInput { newPassword = it.value; passwordError = false }
                            onKeyDown {
                                if (it.key == "Enter" || it.code == "Enter" || it.code == "NumpadEnter") {
                                    it.preventDefault()
                                    doChangePassword()
                                }
                            }
                        })
                        PasswordRequirementHint(newPassword)
                        Input(type = InputType.Password, attrs = {
                            classes(MobileLoginStyles.input, UnifiedAccessStyles.input)
                            placeholder("Wiederholen")
                            value(repeatPassword)
                            onInput { repeatPassword = it.value; passwordError = false }
                            onKeyDown {
                                if (it.key == "Enter" || it.code == "Enter" || it.code == "NumpadEnter") {
                                    it.preventDefault()
                                    doChangePassword()
                                }
                            }
                        })
                        if (passwordError) {
                            Div({ classes(MobileLoginStyles.error) }) {
                                Text(PasswordPolicy.error(newPassword) ?: "Passwörter stimmen nicht überein.")
                            }
                        }
                    }

                    "ZWEI_FAKTOR" -> {
                        ZweiFaktorLoginUi(
                            setup = zweiFaktorEinrichtung,
                            qrCode = zweiFaktorQrCode,
                            code = zweiFaktorCode,
                            trustedDevice = trustedDevice,
                            loading = loading,
                            error = error,
                            errorText = errorText,
                            onCodeChange = {
                                zweiFaktorCode = it
                                error = false
                            },
                            onTrustedDeviceChange = { trustedDevice = it },
                            onSubmit = { submitLogin() }
                        )
                    }

                    "SIGNATUR" -> {}
                }

                if (step != "LOGIN") Button(attrs = {
                    classes(UnifiedAccessStyles.primaryButton)
                    if (loading) disabled()
                    onClick {
                        when (step) {
                            "LOGIN"    -> if (!loading) submitLogin()
                            "PASSWORT" -> doChangePassword()
                            "ZWEI_FAKTOR" -> if (!loading) submitLogin()
                            else                     -> {}
                        }
                    }
                }) {
                    Text(
                        when {
                            loading                          -> "..."
                            step == "LOGIN"    -> "Anmelden"
                            step == "PASSWORT" -> "Weiter"
                            step == "ZWEI_FAKTOR" -> "Code prüfen"
                            else                             -> ""
                        }
                    )
                }
            }
        }
    }

}


object MobileLoginStyles : StyleSheet() {
    private val cBg   = Color("#0b0c0e")
    private val cText = Color("#f5f6f8")

    val fadeIn   by keyframes { from { opacity(0) }; to { opacity(1) } }
    @OptIn(ExperimentalComposeWebApi::class)
    val fadeInUp by keyframes {
        from { opacity(0); transform { translateY(10.px) } }
        to   { opacity(1); transform { translateY(0.px)  } }
    }

    val backgroundStripes by style {
        position(Position.Fixed); top(0.px); left(0.px); width(100.percent); height(100.vh)
        property("background", DashboardTheme.DASHBOARD_BG)
        opacity(0.18)
        property("z-index", 0)
    }

    val stage by style {
        position(Position.Relative)
        property("z-index", 1)
        backgroundColor(Color.transparent); color(cText)
        width(100.vw); height(100.vh)
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
        overflow("hidden")
    }

    val contentWrapper by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        alignItems(AlignItems.Center)
        width(90.percent); maxWidth(410.px)
        padding(28.px)
        property("box-sizing", "border-box")
    }

    val logoPane by style {
        display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); alignItems(AlignItems.Center)
        marginBottom(10.px)
        position(Position.Relative)
    }

    val logoImg by style {
        property("object-fit", "contain")
        width(210.px); height(64.px)
        animation(fadeIn) { duration(600.ms) }
    }

    val brand by style {
        fontSize(24.px); margin(5.px, 0.px)
        paddingTop(24.px)
        backgroundImage("linear-gradient(90deg, #5b9bff, #c8deff)")
        property("-webkit-background-clip", "text"); color(Color.transparent)
        textAlign("center")
    }

    val formPane by style {
        display(DisplayStyle.Flex); flexDirection(FlexDirection.Column)
        width(100.percent); maxWidth(300.px); gap(12.px)
    }

    val input by style {
        width(100.percent); padding(16.px); borderRadius(12.px)
        backgroundColor(rgba(17, 19, 23, 0.38)); color(Color.white); fontSize(16.px)
        property("background", "rgba(17, 19, 23, 0.38)")
        property("border", "1px solid rgba(255,255,255,0.18)"); property("box-shadow", "0 0 0 1px rgba(255,255,255,0.05), 0 0 18px rgba(255,255,255,0.08)"); textAlign("center")
        property("box-sizing", "border-box")
    }

    val inputDelay1 by style { animation(fadeInUp) { duration(400.ms); delay(200.ms); fillMode(AnimationFillMode.Forwards) }; opacity(0) }
    val inputDelay2 by style { animation(fadeInUp) { duration(400.ms); delay(350.ms); fillMode(AnimationFillMode.Forwards) }; opacity(0) }

    val loginBtn by style {
        width(100.percent); height(50.px); borderRadius(12.px)
        fontSize(15.px)
        backgroundColor(Color(UISettings.LOGIN_BUTTON_BG))
        color(Color.white)
        property("background", UISettings.LOGIN_BUTTON_BG)
        property("border", "1px solid ${UISettings.accentRgba(0.72)}")
        property("box-shadow", "0 12px 30px ${UISettings.accentRgba(0.32)}, inset 0 1px 0 rgba(255,255,255,0.18)")
        property("box-sizing", "border-box")
        hover {
            backgroundColor(UISettings.ACCENT)
            color(Color.white)
            property("background", UISettings.ACCENT_HEX)
            property("border-color", "${UISettings.accentRgba(0.9)}")
            property("box-shadow", "0 15px 34px ${UISettings.accentRgba(0.42)}, inset 0 1px 0 rgba(255,255,255,0.22)")
        }
    }

    val error by style { color(Color("#ff4d4d")); fontSize(13.px); textAlign("center"); marginTop(4.px) }

}









